import { useCallback, useEffect, useRef, useState } from "react";

export const CLOSE_ANIM_MS = 250;
export const SWIPE_CLOSE_THRESHOLD = 72;
export const OVERLAY_BASE_ALPHA = 0.5;

export function getOverlayAlpha(offsetY) {
  return Math.max(0.15, OVERLAY_BASE_ALPHA - offsetY / 600);
}

/**
 * Convert 页 bottom sheet 通用交互：Portal 挂载、关闭动画、下滑收起、backdrop 蒙层
 */
export function useConvertBottomSheet({ open, onClose, styles }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [enterDone, setEnterDone] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const sheetRef = useRef(null);
  const dragZoneRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragActiveRef = useRef(false);
  const offsetYRef = useRef(0);
  const onCloseRef = useRef(onClose);
  const closeTimerRef = useRef(null);
  const visibleRef = useRef(false);
  const closingRef = useRef(false);

  onCloseRef.current = onClose;
  visibleRef.current = visible;
  closingRef.current = closing;
  offsetYRef.current = offsetY;

  useEffect(() => {
    if (!visible || typeof document === "undefined") return undefined;
    const scrollTop = document.documentElement.scrollTop;
    document.body.classList.add("at-frozen");
    document.body.style.top = `${-scrollTop}px`;
    return () => {
      document.body.style.top = null;
      document.body.classList.remove("at-frozen");
      document.documentElement.scrollTop = scrollTop;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || enterDone || closing) return undefined;
    const timer = setTimeout(() => setEnterDone(true), 320);
    return () => clearTimeout(timer);
  }, [visible, enterDone, closing]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const finishClose = useCallback(() => {
    clearCloseTimer();
    setVisible(false);
    setClosing(false);
    setEnterDone(false);
    setOffsetY(0);
    setDragging(false);
    dragActiveRef.current = false;
  }, [clearCloseTimer]);

  const getSheetHeight = useCallback(() => {
    const h = sheetRef.current?.offsetHeight;
    return h && h > 0 ? h : 400;
  }, []);

  const closeWithAnimation = useCallback(() => {
    if (closingRef.current || !visibleRef.current) return;
    setEnterDone(true);
    setClosing(true);
    setDragging(false);
    dragActiveRef.current = false;
    onCloseRef.current?.();
    clearCloseTimer();
    const sheetHeight = getSheetHeight();
    setOffsetY(Math.max(offsetYRef.current, sheetHeight));
    closeTimerRef.current = setTimeout(finishClose, CLOSE_ANIM_MS);
  }, [clearCloseTimer, finishClose, getSheetHeight]);

  const closeWithAnimationRef = useRef(closeWithAnimation);
  closeWithAnimationRef.current = closeWithAnimation;

  useEffect(() => {
    if (open) {
      clearCloseTimer();
      setVisible(true);
      setClosing(false);
      setEnterDone(false);
      setOffsetY(0);
      setDragging(false);
      dragActiveRef.current = false;
      return undefined;
    }
    if (visibleRef.current && !closingRef.current) {
      closeWithAnimationRef.current();
    }
    return undefined;
  }, [open, clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const handleSheetAnimationEnd = useCallback((e) => {
    if (e.target !== sheetRef.current) return;
    if (closingRef.current) return;
    if (typeof e.animationName === "string" && !e.animationName.includes("SlideUp")) return;
    setEnterDone(true);
  }, []);

  useEffect(() => {
    const el = dragZoneRef.current;
    if (!visible || closing || !el) return undefined;

    const onTouchStart = (e) => {
      setEnterDone(true);
      dragActiveRef.current = true;
      dragStartYRef.current = e.touches[0].clientY;
      setDragging(true);
    };

    const onTouchMove = (e) => {
      if (!dragActiveRef.current) return;
      const dy = e.touches[0].clientY - dragStartYRef.current;
      if (dy > 0) {
        setOffsetY(dy);
        e.preventDefault();
      } else {
        setOffsetY(0);
      }
    };

    const onTouchEnd = () => {
      if (!dragActiveRef.current) return;
      dragActiveRef.current = false;
      const current = offsetYRef.current;
      setDragging(false);

      if (current > SWIPE_CLOSE_THRESHOLD) {
        closeWithAnimationRef.current();
        return;
      }
      if (current > 0) {
        requestAnimationFrame(() => setOffsetY(0));
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [visible, closing]);

  const overlayClassName = [styles.overlay, closing ? styles["overlay--closing"] : ""]
    .filter(Boolean)
    .join(" ");

  const backdropClassName = [
    styles.backdrop,
    !enterDone && !closing ? styles["backdrop--entering"] : "",
    closing ? styles["backdrop--closing"] : "",
    dragging ? styles["backdrop--dragging"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sheetClassName = [
    styles.sheet,
    !enterDone && !closing ? styles["sheet--entering"] : "",
    closing ? styles["sheet--closing"] : "",
    dragging ? styles["sheet--dragging"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  const backdropStyle = enterDone
    ? { opacity: closing ? 0 : getOverlayAlpha(offsetY) }
    : undefined;

  const sheetStyle =
    enterDone || closing || offsetY > 0
      ? { transform: `translate3d(0, ${offsetY}px, 0)` }
      : undefined;

  return {
    visible,
    sheetRef,
    dragZoneRef,
    closeWithAnimation,
    handleSheetAnimationEnd,
    overlayClassName,
    backdropClassName,
    sheetClassName,
    backdropStyle,
    sheetStyle,
  };
}
